import {
  Alert,
  Box,
  CssBaseline,
  GlobalStyles,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Snackbar,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Fade, { FadeProps } from "@mui/material/Fade";
import type { PimoReactComponent } from "@pimo/pimo-app-builder";
import { SupportedLanguage } from "@pimo/pimo-l10n";
import {
  ContentComponent,
  StepType,
  type TutorialLabels,
  TutorialProvider,
  TutorialScreen,
} from "@pimo/tutorial";
import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { Header, HeaderProps } from "../../molecules/header/header";
import { Sidebar, SidebarProps } from "../../molecules/sidebar/sidebar";
import { SidebarHeader } from "../../molecules/sidebar-header/sidebar-header";
import { PMSetupStepsProps } from "../../pm-components";
import { PMButton } from "../../pm-components/pm-button/pm-button";
import { MenuEntryInterface } from "../../types/menu-type";
import { type Notification } from "../../types/notification";
import { ConfigureDashboardSidebar } from "../configure-dashboard-sidebar/configure-dashboard-sidebar";
import { Log, LogDrawer } from "../log-drawer/log-drawer";

const DEFAULT_SIDEBAR_WIDTH = 266;
const MIN_SIDEBAR_WIDTH = 220;
const MAX_SIDEBAR_WIDTH = 420;
const SIDEBAR_WIDTH_STORAGE_KEY = "pimo:sidebar-width";

const clampSidebarWidth = (width: number) =>
  Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, width));

const getInitialSidebarWidth = () => {
  if (typeof window === "undefined") {
    return DEFAULT_SIDEBAR_WIDTH;
  }

  const rawWidth = window.localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);

  if (!rawWidth) {
    return DEFAULT_SIDEBAR_WIDTH;
  }

  const parsedWidth = Number.parseInt(rawWidth, 10);

  if (Number.isNaN(parsedWidth)) {
    return DEFAULT_SIDEBAR_WIDTH;
  }

  return clampSidebarWidth(parsedWidth);
};

const MainStage = styled("main", {
  shouldForwardProp: (prop) => prop !== "sidebarWidth",
})<{ sidebarWidth: number }>(({ theme, sidebarWidth }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    padding: 0,
  },
  height: "100vh",
  overflow: "auto",
  ml: { md: `-${sidebarWidth}px` },
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  "@media print": {
    height: "auto",
    minHeight: 0,
    maxHeight: "none",
    overflow: "visible",
    margin: 0,
    padding: 0,
    width: "100%",
    position: "static",
    transition: "none",
  },
}));

export interface DefaultOverlayPartialAppState {
  /** is sidebar open */
  isSidebarOpen: boolean;
  snackbar?: {
    open: boolean;
    message: string;
    severity: "info" | "success" | "warning" | "error";
    type: "snackbar" | "alert";
  };
}
export type DefaultOverlayEventNames =
  | "overlay:logout"
  | "overlay:toggle-sidebar"
  | "overlay:click-logo"
  | "overlay:show-logs"
  | "overlay:notification-read"
  | "overlay:close-snackbar"
  | "overlay:configure-my-space-sidebar"
  | "overlay:load-more-logs"
  | "overlay:open-setup-screen";

export type DefaultOverlayNotificationPayload = { notification: Notification };

export type DefaultOverlayEventPayload =
  | undefined
  | DefaultOverlayNotificationPayload
  | {
      isOpen?: boolean;
      entries?: {
        title: string;
        id: string;
        isVisible: boolean;
        order: number;
      }[];
    };

export type DefaultOverlayProps = {
  /** props for the header */
  header: HeaderProps;
  /** Is sidebar open */
  isSidebarOpen: DefaultOverlayPartialAppState["isSidebarOpen"];
  /** Sidebar menu entries */
  menuEntries: MenuEntryInterface[];
  /** title and entries of the right side navigation, if undefined the sidebar will not show  */
  inPageNavigationSidebarProps?: {
    title: string;
    entries: { title: string; id: string }[];
  };
  logs?: Log[];
  notifications?: {
    banner?: Notification;
  };
  configureMySpaceSidebar?: {
    isOpen?: boolean;
    entries?: {
      title: string;
      id: string;
      isVisible: boolean;
      order: number;
    }[];
  };
  snackbar?: {
    message: string;
    severity: "success" | "info" | "warning" | "error";
    open: boolean;
    type: "snackbar" | "alert";
  };
  buttonProps?: React.ComponentProps<typeof PMButton>;
  setupSteps?: Omit<PMSetupStepsProps, "title">;
  // Tutorial Configuration for the @pimo/tutorial package
  tutorial?: {
    // The text for the welcome dialog
    welcomeText: string;
    // The logo for the welcome dialog
    welcomeLogo?: string;
    // Flag indicating wether to show the dialog or not. This can be used to disable the tutorial if it has been skipped or completed by a user.
    showTutorial: boolean;
    // Callback that will be called when the user completes the last step of the tutorial.
    setTutorialCompleted: () => void;
    // The tutorial steps
    steps: StepType[];
    // Optional localized labels for tutorial UI controls.
    labels?: TutorialLabels;
  };
  language?: SupportedLanguage;
} & Pick<SidebarProps, "hasSearchFunctionality" | "searchFunction">;

function FadeTransition(props: FadeProps) {
  return <Fade {...props} timeout={1000} />;
}

export const initialDefaultOverlayState = {
  isSidebarOpen: false,
} satisfies DefaultOverlayPartialAppState;

/** Default Overlay with header, sidebar, and Outlet (react-router) */
export const DefaultOverlay: PimoReactComponent<
  DefaultOverlayProps,
  DefaultOverlayEventNames,
  DefaultOverlayEventPayload
> = ({
  fireEvent,
  hasSearchFunctionality,
  header,
  inPageNavigationSidebarProps,
  isSidebarOpen,
  menuEntries,
  logs,
  searchFunction,
  notifications,
  snackbar,
  buttonProps,
  configureMySpaceSidebar,
  setupSteps,
  tutorial,
  language,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(getInitialSidebarWidth);
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);
  const [isSidebarVisible, setSidebarVisible] = useState(isSidebarOpen);
  const [prevIsSidebarOpen, setPrevIsSidebarOpen] = useState(isSidebarOpen);
  const [snackbarOpen, setSnackbarOpen] = useState(Boolean(snackbar));
  const [prevSnackbar, setPrevSnackbar] = useState(snackbar);
  const [hasMounted, setHasMounted] = useState(false);
  const [prevLocation, setPrevLocation] = useState(location);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(
      SIDEBAR_WIDTH_STORAGE_KEY,
      String(sidebarWidth)
    );
  }, [sidebarWidth]);

  useEffect(() => {
    if (!isResizingSidebar || isMobile) {
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      setSidebarWidth(clampSidebarWidth(event.clientX));
    };

    const handleMouseUp = () => {
      setIsResizingSidebar(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isMobile, isResizingSidebar]);

  if (prevIsSidebarOpen !== isSidebarOpen) {
    setPrevIsSidebarOpen(isSidebarOpen);
    setSidebarVisible(isSidebarOpen);
  }

  if (prevSnackbar !== snackbar) {
    setPrevSnackbar(snackbar);
    setSnackbarOpen(snackbar?.open ?? false);
  }

  /**
   * Manage sidebar visibility:
   * - Close sidebar on route change (only for mobile).
   */
  if (!hasMounted) {
    setHasMounted(true);
    if (isMobile) {
      setSidebarVisible(true);
    }
  } else if (prevLocation !== location) {
    setPrevLocation(location);
    if (isMobile) {
      setSidebarVisible(false);
    }
  }

  const handleSidebarToggle = () => {
    setSidebarVisible((prev) => !prev);
    fireEvent?.("overlay:toggle-sidebar");
  };

  const handleSidebarResizeStart = (
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    event.preventDefault();
    setIsResizingSidebar(true);
  };

  const banner = notifications?.banner,
    bannerIsPublished =
      banner && (banner.published || banner.publishBannerNotification);

  return (
    <div data-testid="DefaultOverlay">
      <GlobalStyles
        styles={{
          "@page": {
            size: "A4 landscape",
            margin: "10mm",
          },
          "@media print": {
            "html, body, #root": {
              width: "100% !important",
              height: "auto !important",
              minHeight: "0 !important",
              overflow: "visible !important",
            },
            "*, *::before, *::after": {
              WebkitPrintColorAdjust: "exact",
              printColorAdjust: "exact",
              animation: "none !important",
              transition: "none !important",
            },
            "[data-testid='DefaultOverlay']": {
              display: "block !important",
            },
            "header, nav, [role='separator'], .no-print": {
              display: "none !important",
            },
            "[role='tablist']": {
              display: "none !important",
            },
            main: {
              width: "100% !important",
              height: "auto !important",
              minHeight: "0 !important",
              maxHeight: "none !important",
              margin: "0 !important",
              padding: "0 !important",
              overflow: "visible !important",
              position: "static !important",
            },
            ".MuiCard-root": {
              boxShadow: "none !important",
              border: "1px solid #c7ced8 !important",
              breakInside: "avoid",
              pageBreakInside: "avoid",
            },
            "[data-pimo-print-stage]": {
              height: "auto !important",
              minHeight: "0 !important",
              maxHeight: "none !important",
              overflow: "visible !important",
            },
            "[data-pimo-print-background]": {
              WebkitBoxDecorationBreak: "clone",
              boxDecorationBreak: "clone",
            },
            "[data-pimo-dnd-container]": {
              display: "block !important",
              height: "auto !important",
              minHeight: "0 !important",
              maxHeight: "none !important",
              overflow: "visible !important",
            },
            "[data-pimo-dnd-background]": {
              display: "block !important",
              height: "auto !important",
              minHeight: "0 !important",
              maxHeight: "none !important",
              overflow: "visible !important",
              WebkitBoxDecorationBreak: "clone",
              boxDecorationBreak: "clone",
            },
            "[data-pimo-dnd-toolbar]": {
              display: "none !important",
            },
            "[data-pimo-dnd-column]": {
              background: "transparent !important",
            },
            "[data-pimo-dnd-column] [data-component-id] > .MuiCard-root": {
              height: "100% !important",
              boxSizing: "border-box",
            },
            "[data-pimo-dnd-stage]": {
              display: "block !important",
              flex: "none !important",
              height: "auto !important",
              minHeight: "0 !important",
              maxHeight: "none !important",
              overflow: "visible !important",
            },
            "svg, canvas, img": {
              maxWidth: "100%",
            },
          },
        }}
      />
      <Box
        sx={{
          display: "flex",
          mt: `env(safe-area-inset-top) !important`,
          "@media print": {
            display: "block",
          },
        }}
      >
        <TutorialProvider
          steps={tutorial?.steps ?? []}
          ContentComponent={(props) => (
            <ContentComponent
              setTutorialCompleted={tutorial?.setTutorialCompleted}
              labels={tutorial?.labels}
              language={language}
              {...props}
            />
          )}
        >
          {tutorial?.showTutorial && (
            <TutorialScreen
              stateSteps={tutorial.steps}
              setTutorialCompleted={tutorial.setTutorialCompleted}
              text={tutorial.welcomeText}
              logo={tutorial.welcomeLogo}
              language={language}
              labels={tutorial.labels}
            />
          )}
          <CssBaseline />
          <Header
            {...header}
            banner={bannerIsPublished ? banner : undefined}
            handleSidebarToggle={handleSidebarToggle}
            handleLogoClick={() => fireEvent?.("overlay:click-logo")}
            onLogsShow={() => {
              fireEvent?.("overlay:show-logs");
              setOpen(!open);
            }}
            onNotificationRead={(notification) =>
              fireEvent?.("overlay:notification-read", { notification })
            }
          />
          <Box
            component="nav"
            sx={{
              position: "relative",
              width: {
                xs: 0,
                sm: isSidebarVisible ? sidebarWidth : 0,
              },
              flexShrink: { xs: 0 },
            }}
            aria-label="sidebar menu"
          >
            <Sidebar
              entries={menuEntries}
              hasSearchFunctionality={hasSearchFunctionality}
              searchFunction={searchFunction}
              sidebarOpen={isSidebarVisible}
              width={isMobile ? DEFAULT_SIDEBAR_WIDTH : sidebarWidth}
              hasBanner={bannerIsPublished}
              buttonProps={buttonProps}
              setupSteps={setupSteps}
              openSetupScreen={() => fireEvent?.("overlay:open-setup-screen")}
              language={language}
            />
            {isSidebarVisible && !isMobile && (
              <Box
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize sidebar"
                onMouseDown={handleSidebarResizeStart}
                sx={{
                  position: "absolute",
                  top: 0,
                  right: -3,
                  bottom: 0,
                  width: 6,
                  cursor: "col-resize",
                  zIndex: (t) => t.zIndex.drawer + 1,
                  backgroundColor: isResizingSidebar
                    ? theme.palette.primary.main
                    : "transparent",
                  opacity: isResizingSidebar ? 0.4 : 1,
                  transition: "background-color 0.15s ease",
                }}
              />
            )}
          </Box>
          <MainStage sidebarWidth={sidebarWidth}>
            <SidebarHeader hasBanner={bannerIsPublished} />
            <Outlet />
          </MainStage>
          {inPageNavigationSidebarProps && (
            <Box
              sx={{
                height: "100vh",
                minWidth: 300,
                maxWidth: 300,
                [theme.breakpoints.down("sm")]: {
                  display: "none",
                },
                "@media print": {
                  display: "none",
                },
              }}
            >
              <SidebarHeader />
              <Typography sx={{ fontWeight: 500, fontSize: "1.2rem", mb: 1 }}>
                {inPageNavigationSidebarProps.title}
              </Typography>
              <List
                sx={{
                  borderLeft: "3px solid",
                  borderColor: theme.palette.primary.main,
                }}
              >
                {inPageNavigationSidebarProps.entries.map((entry) => (
                  <ListItem
                    onClick={() => {
                      const section = document.getElementById(entry.id);

                      if (section) {
                        section.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }
                    }}
                    key={entry.id}
                    disablePadding
                  >
                    <ListItemButton>
                      <ListItemText primary={entry.title} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          {configureMySpaceSidebar && (
            <ConfigureDashboardSidebar
              configureMySpaceSidebar={configureMySpaceSidebar}
              onClose={() => {
                fireEvent?.("overlay:configure-my-space-sidebar", {
                  entries: configureMySpaceSidebar.entries,
                  isOpen: false,
                });
              }}
              onUpdate={(entries) => {
                fireEvent?.("overlay:configure-my-space-sidebar", {
                  entries,
                  isOpen: true,
                });
              }}
            ></ConfigureDashboardSidebar>
          )}
          <LogDrawer
            logs={logs}
            open={open}
            setOpen={setOpen}
            onLoadMore={() => fireEvent?.("overlay:load-more-logs")}
          />
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            onClose={() => {
              setSnackbarOpen(false);
              setTimeout(() => {
                fireEvent?.("overlay:close-snackbar");
              }, 1000);
            }}
            TransitionComponent={FadeTransition}
          >
            <Alert
              severity={snackbar?.severity}
              variant="filled"
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                color: "white",
              }}
            >
              {snackbar?.message}
            </Alert>
          </Snackbar>
        </TutorialProvider>
      </Box>
    </div>
  );
};
